import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const timeOptions = [
  '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
  '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
  '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM'
];

const VendorSelection = () => {
  const { serviceDetailId } = useParams();
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Get vendor_id from logged in user
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const vendor_id = user.id;

  useEffect(() => {
  fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/vendor_selection/schedule?vendor_id=${vendor_id}&service_detail_id=${serviceDetailId}`)
      .then(res => res.json())
      .then(data => {
        setSchedule(data.schedule || {});
        setLoading(false);
      })
      .catch(() => {
        setSchedule({});
        setLoading(false);
      });
  }, [vendor_id, serviceDetailId]);

  const handleChange = (day, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
    setError('');
    setSuccess('');
  };

  const handleReset = (day) => {
    if (window.confirm(`Are you sure you want to reset the schedule for ${day}?`)) {
      setSchedule(prev => ({
        ...prev,
        [day]: {
          start_time: '',
          end_time: '',
          hourly_rate: '',
        }
      }));
      setError('');
      setSuccess('');
    }
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    for (const day of daysOfWeek) {
      const start = schedule[day]?.start_time;
      const end = schedule[day]?.end_time;
      if (start && end) {
        const startMin = parseTime(start);
        const endMin = parseTime(end);
        if (startMin >= endMin) {
          setError(`Start time must be earlier than end time for ${day}.`);
          return;
        }
      }
    }

    setLoading(true);
    try {
      let anySaved = false;
      for (const day of daysOfWeek) {
        const start = schedule[day]?.start_time;
        const end = schedule[day]?.end_time;
        const hourly_rate = schedule[day]?.hourly_rate;
        if (start && end) {
          await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/save_vendor_selection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vendor_id,
              service_detail_id: serviceDetailId,
              service_id: schedule[day]?.service_id || '',
              day_of_month: day,
              start_time: start,
              end_time: end,
              hourly_rate: hourly_rate !== undefined ? hourly_rate : '',
            }),
          });
          anySaved = true;
        } else {
          await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/delete_vendor_selection`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vendor_id,
              service_detail_id: serviceDetailId,
              service_id: schedule[day]?.service_id || '',
              day_of_month: day,
            }),
          });
        }
      }
      setLoading(false);
      if (anySaved) {
        setSuccess('Schedule updated!');
      } else {
        setError('All Schedules have been cleared. Please add new schedules when you are ready to teach.');
      }
    } catch {
      setLoading(false);
      setError('Error updating schedule.');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loader}>
          <span className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#8d6e36' }}></span>
          <div style={{ marginTop: '12px', color: '#8d6e36', fontWeight: 'bold' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.iconCircle}>
          <i className="fas fa-calendar-alt" style={styles.icon}></i>
        </span>
        <h2 style={styles.title}>Service Schedule Details</h2>
        <p style={styles.subtitle}>
          Set your available time slots and hourly rate for each day of the week.
        </p>
      </div>
      <button
        style={styles.backBtn}
        type="button"
        onClick={() => navigate(-1)}
      >
        &#8592; Back
      </button>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}
      <div style={styles.scheduleGrid}>
        {daysOfWeek.map(day => (
          <div key={day} style={styles.dayRow}>
            <span style={styles.dayName}>{day}</span>
            <span style={styles.timeLabel}>Start:</span>
            <select
              value={schedule[day]?.start_time || ''}
              onChange={e => handleChange(day, 'start_time', e.target.value)}
              style={styles.select}
            >
              <option value="">Select</option>
              {timeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <span style={styles.timeLabel}>End:</span>
            <select
              value={schedule[day]?.end_time || ''}
              onChange={e => handleChange(day, 'end_time', e.target.value)}
              style={styles.select}
            >
              <option value="">Select</option>
              {timeOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <span style={styles.timeLabel}>Hourly:</span>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={styles.dollar}>$</span>
              <input
                type="number"
                min="0"
                style={styles.hourlyRateInput}
                value={schedule[day]?.hourly_rate || ''}
                onChange={e => handleChange(day, 'hourly_rate', e.target.value)}
                placeholder="Rate"
              />
            </div>
            <button
              style={styles.resetBtn}
              type="button"
              onClick={() => handleReset(day)}
            >
              Reset
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '18px', marginTop: '28px' }}>
        <button style={styles.button} onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save Schedule'}
        </button>
        <button
          style={styles.cancelBtn}
          type="button"
          onClick={() => navigate(-1)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '48px auto',
    padding: '48px',
    background: '#fff',
    borderRadius: '18px',
    boxShadow: '0 4px 24px rgba(141,110,54,0.10)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  iconCircle: {
    background: '#4078c0',
    borderRadius: '50%',
    width: '54px',
    height: '54px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
    boxShadow: '0 2px 8px #b3c6e6',
  },
  icon: {
    color: '#fff',
    fontSize: '2rem',
    textShadow: '0 1px 4px #b3c6e6',
  },
  title: {
    color: '#4078c0',
    fontSize: '1.7rem',
    fontWeight: 'bold',
    marginBottom: '8px',
    marginTop: '0',
  },
  subtitle: {
    color: '#8d6e36',
    fontSize: '1.08rem',
    marginBottom: '0',
    marginTop: '0',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 0',
  },
  error: {
    color: '#c0392b',
    background: '#fff6f6',
    borderRadius: '6px',
    padding: '10px',
    marginBottom: '16px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    border: '1px solid #f8d7da',
  },
  success: {
    color: '#388e3c',
    background: '#f1f8e9',
    borderRadius: '6px',
    padding: '10px',
    marginBottom: '16px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    border: '1px solid #c8e6c9',
  },
  scheduleGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
  dayRow: {
    display: 'flex',
    alignItems: 'center',
    background: '#f5f6fa',
    borderRadius: '10px',
    padding: '16px 22px',
    boxShadow: '0 1px 4px rgba(64,120,192,0.07)',
    fontSize: '1.08rem',
    marginBottom: '8px',
    transition: 'box-shadow 0.2s',
  },
  dayName: {
    fontWeight: 'bold',
    color: '#8d6e36',
    minWidth: '110px',
    fontSize: '1.08rem',
    letterSpacing: '0.5px',
  },
  timeLabel: {
    color: '#4078c0',
    marginLeft: '18px',
    marginRight: '6px',
    fontWeight: 'bold',
    fontSize: '1.08rem',
  },
  select: {
    minWidth: '110px',
    padding: '8px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '1.08rem',
    marginRight: '12px',
    background: '#fff',
    boxShadow: '0 1px 4px #e0e0e0',
  },
  dollar: {
    color: '#8d6e36',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    marginRight: '6px',
  },
  hourlyRateInput: {
    width: '90px', // Slightly bigger rate box
    minWidth: '90px',
    padding: '8px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '1.08rem',
    background: '#fff',
    boxShadow: '0 1px 4px #e0e0e0',
    textAlign: 'right',
  },
  button: {
    marginTop: '28px',
    background: '#8d6e36',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 0',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    cursor: 'pointer',
    width: '100%',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
  },
  resetBtn: {
    background: '#e57373',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 18px',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    cursor: 'pointer',
    marginLeft: '18px',
    boxShadow: '0 1px 4px #ffcdd2',
    transition: 'background 0.2s',
  },
  cancelBtn: {
    marginTop: '28px',
    background: '#8d6e36',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 0',
    fontWeight: 'bold',
    fontSize: '1.08rem',
    cursor: 'pointer',
    width: '100%',
    boxShadow: '0 1px 4px #e0cfa0',
    transition: 'background 0.2s',
  },
  backBtn: {
    background: '#4078c0',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 22px',
    fontWeight: 'bold',
    fontSize: '1rem',
    cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(64,120,192,0.07)',
    marginBottom: '18px',
    marginTop: '-10px',
    display: 'inline-block',
  },
};

export default VendorSelection;